import { AI_HELP_YARL_STYLES } from '@constants/layout'
import { IFile } from '@query'
import React, { useCallback, useMemo, useState } from 'react'
import { MalwareScanNotice, attachmentScanBlock } from './MalwareScanNotice'
import Lightbox from 'yet-another-react-lightbox'
import { BlurhashCanvas } from 'react-blurhash'
import { FaTimes } from 'react-icons/fa'
import styled from 'styled-components'

interface FilePreviewProps {
  file: IFile
  showRemove?: boolean
  onRemove?: () => void
  showHeader?: boolean
  showNoPreview?: boolean
}

export const FilePreview2: React.FC<FilePreviewProps> = ({
  file,
  showHeader,
  showRemove,
  onRemove,
  showNoPreview = false
}) => {
  const [lightBoxShown, setLightBoxShown] = useState(false)
  const unsignedFileUrl = file.file?.split('?')[0]
  const fileSrc = useMemo(() => file.file, [file.id, unsignedFileUrl, new Date().getHours()])
  const scanBlock = attachmentScanBlock(file.malware_scan_status)
  const fileType = file.type?.toLowerCase()
  const isImage = fileType?.includes('image') ?? false
  const isPdf = fileType === 'application/pdf'

  const scale = useCallback((number, inMin, inMax, outMin, outMax) => {
    return ((number - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin
  }, [])

  const blurhashW = useMemo(
    () =>
      file.blurhash_w && file.blurhash_h
        ? Math.round(scale(file.blurhash_w, 0, Math.max(file.blurhash_w, file.blurhash_h), 0, 32))
        : undefined,
    [file.id]
  )
  const blurhashH = useMemo(
    () =>
      file.blurhash_w && file.blurhash_h
        ? Math.round(scale(file.blurhash_h, 0, Math.max(file.blurhash_w, file.blurhash_h), 0, 32))
        : undefined,
    [file.id]
  )

  if (scanBlock) {
    return (
      <>
        {showHeader && (
          <StyledHeader>
            {file.name}
            {showRemove && <StyledRemove onClick={() => onRemove()} />}
          </StyledHeader>
        )}
        <MalwareScanNotice status={file.malware_scan_status} />
      </>
    )
  }

  if (isImage && fileSrc) {
    return (
      <>
        {showHeader && (
          <StyledHeader>
            {file.name}
            {showRemove && <StyledRemove onClick={() => onRemove()} />}
          </StyledHeader>
        )}
        <StyledCard>
          <ImagePreviewWrapper onClick={() => setLightBoxShown(true)}>
            {file.blurhash && (
              <ImagePreview key="blurhash">
                <BlurhashCanvas
                  hash={file.blurhash}
                  width={blurhashW}
                  height={blurhashH}
                  style={{
                    height: '100%',
                    width: '100%',
                    objectFit: 'contain'
                  }}
                />
              </ImagePreview>
            )}
            <ImagePreview
              key="content"
              $url={fileSrc}
              $imageW={file.blurhash_w}
              $imageH={file.blurhash_h}
            />
          </ImagePreviewWrapper>

          <Lightbox
            open={lightBoxShown}
            close={() => setLightBoxShown(false)}
            slides={[{ src: fileSrc }]}
            controller={{ closeOnBackdropClick: true }}
            render={{ buttonPrev: () => null, buttonNext: () => null }}
            styles={AI_HELP_YARL_STYLES}
          />
        </StyledCard>
      </>
    )
  }

  if (isPdf && fileSrc) {
    return (
      <>
        {showHeader && (
          <StyledHeader>
            {file.name}
            {showRemove && <StyledRemove onClick={() => onRemove()} />}
          </StyledHeader>
        )}
        <StyledCard $isClickable={false}>
          <StyledEmbedWrapper>
            <StyledEmbed src={fileSrc} type={file.type} />
          </StyledEmbedWrapper>
        </StyledCard>
      </>
    )
  }

  if (showNoPreview) {
    return <NoPreview>{`Tiedostoa ${file.name} ei voida esikatsella`}</NoPreview>
  }

  return null
}

const ImagePreviewWrapper = styled.div`
  width: 100%;
  position: relative;
  padding-bottom: 100%;
`

const ImagePreview = styled.div<{ $url?: string; $imageW?: number; $imageH?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  background-image: url(${({ $url }) => $url});
  width: 100%;
  height: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
`

const StyledEmbedWrapper = styled.div`
  width: 100%;
  position: relative;
  padding-bottom: 70.7%;
`

const StyledEmbed = styled.embed`
  border: none;
  position: absolute;
  width: 100%;
  height: 100%;
`

const StyledCard = styled.div<{ $isClickable?: boolean }>`
  display: flex;
  flex: 1;
  position: relative;
  justify-content: center;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  cursor: ${({ $isClickable = true }) => ($isClickable ? 'pointer' : 'default')};
  background: ${({ theme }) => theme.colors.neutralWhite};
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
`

const StyledHeader = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.metalGray};
  margin-bottom: ${({ theme }) => theme.spacing.xs}rem;
`

const StyledRemove = styled(FaTimes)``

const NoPreview = styled.div`
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl}rem ${({ theme }) => theme.spacing.md}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
`
